'use client'

import { useEffect, useRef, useState } from 'react'
import { useLocale } from 'next-intl'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useEntitlements } from '@/hooks/useEntitlements'
import { LoginDialog } from '@/components/auth/login-dialog'
import { analytics } from '@/lib/analytics'
import { createCheckoutSession } from '@/lib/billing'
import { PRO_FEATURES, type PaywallType } from '@/lib/monetization'
import { cn } from '@/lib/utils'
import { IconArrowRight, IconCheck, IconLoader2 } from '@tabler/icons-react'

export interface UpgradeIntentContext {
  [key: string]: unknown
}

interface UpgradeIntentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  paywallType: PaywallType
  surface: string
  context?: UpgradeIntentContext
  title?: string
  description?: string
  submitLabel?: string
}

export function UpgradeIntentDialog({
  open,
  onOpenChange,
  paywallType,
  surface,
  context,
  title,
  description,
  submitLabel
}: UpgradeIntentDialogProps) {
  const locale = useLocale()
  const { user } = useAuth()
  const { dodoBillingEnabled, pricing } = useEntitlements()
  const [selectedPlan, setSelectedPlan] = useState<'weekly' | 'yearly'>(
    'yearly'
  )
  const [checkoutPlanLoading, setCheckoutPlanLoading] = useState<
    'weekly' | 'yearly' | null
  >(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const hasTrackedViewRef = useRef(false)

  useEffect(() => {
    if (!open) {
      hasTrackedViewRef.current = false
      return
    }
    if (hasTrackedViewRef.current) return
    hasTrackedViewRef.current = true
    analytics.paywallViewed(paywallType, surface, context)
  }, [open, paywallType, surface, context])

  const dialogTitle = title ?? 'Unlock Pro stories'
  const weeklyLabel = pricing.weeklyUsd.toFixed(2)
  const yearlyLabel = pricing.yearlyUsd.toFixed(2)
  const yearlyWeeklyLabel = (pricing.yearlyUsd / 52).toFixed(2)
  const yearlySavings = Math.max(
    0,
    pricing.weeklyUsd * 52 - pricing.yearlyUsd
  ).toFixed(2)
  const canUseCheckout = dodoBillingEnabled && Boolean(user)
  const isYearly = selectedPlan === 'yearly'
  const ctaLabel =
    submitLabel ?? (isYearly ? 'Start Pro yearly' : 'Start Pro weekly')

  const resetState = () => {
    setCheckoutPlanLoading(null)
    setCheckoutError(null)
    setSelectedPlan('yearly')
  }

  const handleCheckout = async (plan: 'weekly' | 'yearly') => {
    if (!user || !canUseCheckout) return

    setCheckoutPlanLoading(plan)
    setCheckoutError(null)

    try {
      analytics.checkoutStarted(plan, paywallType, surface, context)

      const result = await createCheckoutSession({
        plan,
        paywallType,
        surface,
        context: {
          ...(context ?? {}),
          locale
        }
      })

      analytics.checkoutRedirected(plan, paywallType, surface, {
        checkout_url: result.checkout_url
      })

      window.location.href = result.checkout_url
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Could not start checkout. Please try again.'
      setCheckoutError(message)
      toast.error(message)
      setCheckoutPlanLoading(null)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) resetState()
      }}
    >
      <DialogContent className="max-h-[min(92dvh,720px)] gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="border-b px-5 pt-6 pb-5 text-left sm:px-7">
          <DialogTitle className="text-2xl leading-tight">
            {dialogTitle}
          </DialogTitle>
          <p className="text-muted-foreground mt-2 text-sm leading-6">
            {description ??
              'The dictionary, document reader, and offline saves are free. Pro unlocks selected stories and chapters.'}
          </p>
        </DialogHeader>

        <div className="max-h-[calc(min(92dvh,720px)-82px)] overflow-y-auto">
          <section className="px-5 py-4 sm:px-7">
            <div className="space-y-4">
              {PRO_FEATURES.map((feature) => {
                const requested = feature.paywallTypes.some(
                  (type) => type === paywallType
                )
                return (
                  <div
                    key={feature.title}
                    className={cn(
                      'flex min-w-0 items-start gap-3 rounded-xl px-3 py-2.5',
                      requested && 'bg-primary/5'
                    )}
                  >
                    <span
                      className={cn(
                        'bg-background text-muted-foreground mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border',
                        requested && 'border-primary/25 text-primary'
                      )}
                      aria-hidden="true"
                    >
                      <IconCheck className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm leading-5 font-semibold">
                        {feature.title}
                      </p>
                      <p className="text-muted-foreground mt-0.5 text-sm leading-6">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          <aside className="border-border/70 bg-muted/20 border-t px-5 py-5 sm:px-7">
            <div className="space-y-4">
              {!dodoBillingEnabled && (
                <p className="border-destructive/30 bg-destructive/5 text-destructive rounded-xl border p-3 text-sm">
                  Billing is currently disabled. Enable
                  NEXT_PUBLIC_DODO_BILLING_ENABLED and redeploy.
                </p>
              )}

              <div className="space-y-3">
                <div className="grid gap-2">
                  {(['yearly', 'weekly'] as const).map((plan) => {
                    const active = selectedPlan === plan
                    const planPrice =
                      plan === 'yearly' ? yearlyWeeklyLabel : weeklyLabel
                    const planMeta =
                      plan === 'yearly'
                        ? `$${yearlyLabel} billed yearly`
                        : 'Billed weekly'
                    const planLabel = plan === 'yearly' ? 'Yearly' : 'Weekly'

                    return (
                      <button
                        key={plan}
                        type="button"
                        className={cn(
                          'bg-background rounded-xl border p-3 text-left transition-colors',
                          active
                            ? 'border-primary/55 ring-primary/15 ring-4'
                            : 'border-border hover:border-foreground/25'
                        )}
                        onClick={() => setSelectedPlan(plan)}
                        disabled={checkoutPlanLoading !== null}
                      >
                        <span className="flex items-start justify-between gap-3">
                          <span>
                            <span className="block text-sm font-semibold">
                              {planLabel}
                            </span>
                            <span className="text-muted-foreground mt-1 block text-xs">
                              {planMeta}
                            </span>
                          </span>
                          <span className="text-right">
                            <span className="block text-lg leading-none font-semibold">
                              ${planPrice}
                            </span>
                            <span className="text-muted-foreground mt-1 block text-xs">
                              {plan === 'yearly' ? '/wk equivalent' : '/wk'}
                            </span>
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>

                {Number(yearlySavings) > 0 && (
                  <p className="text-muted-foreground px-1 text-xs">
                    Yearly saves ${yearlySavings} compared with weekly.
                  </p>
                )}

                {canUseCheckout ? (
                  <Button
                    className="h-11 w-full rounded-full"
                    onClick={() => void handleCheckout(selectedPlan)}
                    disabled={checkoutPlanLoading !== null}
                  >
                    {checkoutPlanLoading ? (
                      <>
                        <IconLoader2 className="h-4 w-4 animate-spin" />
                        Starting checkout
                      </>
                    ) : (
                      <>
                        {ctaLabel}
                        <IconArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                ) : dodoBillingEnabled ? (
                  <LoginDialog>
                    <Button className="h-11 w-full rounded-full">
                      Sign in to upgrade
                    </Button>
                  </LoginDialog>
                ) : null}

                {checkoutError && (
                  <p className="text-destructive mt-3 text-sm">
                    {checkoutError}
                  </p>
                )}

                <p className="text-muted-foreground px-1 text-center text-xs leading-5">
                  Secure checkout. Cancel from Settings.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  )
}
